import React, { useEffect, useRef } from 'react';
import { BsPencilSquare, BsPencil, BsChatDots, BsListTask, BsGear } from "react-icons/bs";
import { User } from 'firebase/auth';
import { ToolType } from './ToolsPanel';
import './ToolPicker.css';

// Tool configuration interface
interface ToolConfig {
  id: ToolType;
  name: string;
  icon: React.ReactNode;
  requiresSubscription?: boolean;
}

interface ToolPickerProps {
  isOpen: boolean;
  onClose: () => void;
  activeTool: ToolType;
  onToolSelect: (toolId: ToolType) => void;
  user: User | null;
  hasPaidSubscription: boolean;
  onShowPaymentPopup: () => void;
  onShowLoginInfo: () => void;
  isWalkthroughMode?: boolean;
  enabledTools: { [key: string]: boolean };
}

const ToolPicker: React.FC<ToolPickerProps> = ({
  isOpen,
  onClose,
  activeTool,
  onToolSelect,
  user,
  hasPaidSubscription,
  onShowPaymentPopup,
  onShowLoginInfo,
  isWalkthroughMode = false,
  enabledTools
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Tool configurations
  const toolConfigs: ToolConfig[] = [
    {
      id: 'notes',
      name: 'Notes',
      icon: <BsPencil />,
      requiresSubscription: true
    },
    {
      id: 'chat',
      name: 'Community Chat',
      icon: <BsChatDots />,
      requiresSubscription: true
    },
    // Future tools can be added here:
    // {
    //   id: 'tasks',
    //   name: 'Tasks',
    //   icon: <BsListTask />,
    //   requiresSubscription: true
    // }
  ];

  // Filter tools based on enabled settings
  const availableTools = toolConfigs.filter(tool => enabledTools[tool.id] !== false);
  
  // Don't show any fallback - if no tools are enabled, show empty state
  const finalToolConfigs = availableTools;

  // Handle clicks outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (overlayRef.current && !overlayRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle tool selection
  const handleToolClick = (toolId: ToolType) => {
    const toolConfig = finalToolConfigs.find(tool => tool.id === toolId);
    if (!toolConfig) return;

    // Check subscription requirements
    if (!isWalkthroughMode && toolConfig.requiresSubscription) {
      if (!user) {
        onShowLoginInfo();
        return;
      }
      if (!hasPaidSubscription) {
        onShowPaymentPopup();
        return;
      }
    }

    onToolSelect(toolId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="tool-picker-overlay">
      <div ref={overlayRef} className="tool-picker">
        <div className="tool-picker-header">
          <h3>Choose your tool</h3>
        </div>
        
        <div className="tool-picker-grid">
          {finalToolConfigs.length > 0 ? (
            finalToolConfigs.map((tool) => (
              <button
                key={tool.id}
                className={`tool-picker-item ${activeTool === tool.id ? 'active' : ''}`}
                onClick={() => handleToolClick(tool.id)}
              >
                <div className="tool-picker-icon">
                  {tool.icon}
                </div>
                <span className="tool-picker-name">{tool.name}</span>
              </button>
            ))
          ) : (
            <div className="tool-picker-empty">
              <p>No tools are currently enabled.</p>
              <p>Enable tools in Settings to access them here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolPicker; 